import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchMe, signOut as clearToken, type SessionUser } from "../api/auth";
import { ApiError, authToken, onUnauthorized } from "../api/client";
import { readJsonPref, writePref } from "./prefs";
import { resyncPush } from "./push";

/*
 * The last signed-in user (name, email, role, track: display fields only, never progress), so reopening the installed
 * app offline still shows the participant their shell instead of the sign-in form. Replaced by the live `me` as soon as
 * the network answers; cleared on sign-out and on any 401.
 */
const CACHED_USER = "sessionUser";

/*
 * Who is signed in. prepai had no session layer at all, so a signed-out person could open any route and just see an
 * error card. This loads `me` once, exposes it to the route guards, and clears it on any 401 so an expired session
 * lands back on sign-in instead of failing screen by screen.
 */
interface SessionState {
  user: SessionUser | null;
  loading: boolean;
  setUser: (user: SessionUser | null) => void;
  signOut: () => void;
  reload: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<SessionUser | null>(null);

  const setUser = useCallback((next: SessionUser | null) => {
    writePref(CACHED_USER, next ? JSON.stringify(next) : null);
    setUserState(next);
  }, []);
  const [loading, setLoading] = useState(() => Boolean(authToken.get()));

  const reload = useCallback(async () => {
    if (!authToken.get()) {
      setUser(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setUser(await fetchMe());
    } catch (error) {
      // Offline with a token: keep the person "signed in" so saved drafts and the shell still work.
      if (error instanceof ApiError && error.isNetwork)
        setUserState(readJsonPref<SessionUser>(CACHED_USER));
      else setUser(null);
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  const signOut = useCallback(() => {
    clearToken();
    setUser(null);
  }, [setUser]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => onUnauthorized(signOut), [signOut]);

  const participantId = user?.role === "Participant" ? user.id : null;
  useEffect(() => {
    if (participantId) void resyncPush();
  }, [participantId]);

  const value = useMemo(
    () => ({ user, loading, setUser, signOut, reload }),
    [user, loading, setUser, signOut, reload],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const session = useContext(SessionContext);
  if (!session) throw new Error("useSession must be used inside <SessionProvider>");
  return session;
}
